import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuthStore from '../store/authStore';
import useTenantStore from '../store/tenantStore';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import { enrollmentService } from '../services/api';
import { Card, Badge, EmptyState, Button, FullPageSpinner } from '../components/ui';
import { useNotification } from '../contexts/NotificationContext';
import { getApiErrorMessage } from '../utils/apiErrors';

function MyEnrollments() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const certificatesEnabled = useTenantStore((state) => state.isFeatureEnabled('certificates'));
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const [highlightId, setHighlightId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [requestingId, setRequestingId] = useState(null);
  const { addNotification } = useNotification();

  const handleRequestCertificate = async (enrollmentId) => {
    setRequestingId(enrollmentId);
    try {
      await enrollmentService.requestCertificate(enrollmentId);
      setEnrollments((items) => items.map((item) => (
        item.id === enrollmentId ? { ...item, certificate_status: 'pending' } : item
      )));
      addNotification(t('Certificate request sent to your teacher.'), 'success');
    } catch (err) {
      addNotification(getApiErrorMessage(err, t, 'Failed to request certificate.'), 'error');
    } finally {
      setRequestingId(null);
    }
  };

  const handleDownloadCertificate = async (enrollmentId) => {
    setDownloadingId(enrollmentId);
    try {
      const res = await enrollmentService.downloadCertificate(enrollmentId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate-${enrollmentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download certificate', err);
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    enrollmentService.getMyEnrollments()
      .then((res) => {
        if (!mounted) return;
        const data = res.data;
        const items = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
        setEnrollments(items);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err);
      })
      .finally(() => mounted && setLoading(false));

    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const eid = params.get('enrollment');
    if (eid) {
      setHighlightId(Number(eid));
    }
  }, [location.search]);

  useEffect(() => {
    if (!highlightId || enrollments.length === 0) return;
    const el = document.getElementById(`enrollment-row-${highlightId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => setHighlightId(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [enrollments, highlightId]);

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  if (loading) return <FullPageSpinner />;
  if (error) {
    return (
      <div className="flex h-screen bg-surface-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopNav title={t('My Enrollments')} />
          <main className="flex-1 flex items-center justify-center p-6">
            <EmptyState title={t('Failed to load enrollments')} />
          </main>
        </div>
      </div>
    );
  }

  const counts = enrollments.reduce((acc, e) => {
    const key = String(e.status || '').toLowerCase();
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const STATUS_ORDER = ['active', 'pending', 'expired', 'cancelled', 'rejected'];

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('My Enrollments')} subtitle={t('Enrollment history and status')} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="max-w-4xl mx-auto">
            {enrollments.length === 0 ? (
              <Card>
                <EmptyState
                  title={t('You have no enrollments yet.')}
                  description={t('Browse the course store to enroll in your first module.')}
                  action={<Button onClick={() => navigate('/store')}>{t('Browse Courses')}</Button>}
                />
              </Card>
            ) : (
              <>
                <div className="flex flex-wrap gap-2 mb-6">
                  {STATUS_ORDER.filter((s) => counts[s]).map((s) => (
                    <div key={s} className="flex items-center gap-2 bg-white border border-gray-100 rounded-full pl-3 pr-3.5 py-1.5 shadow-sm">
                      <Badge status={s} />
                      <span className="text-sm font-semibold text-gray-700">{counts[s]}</span>
                    </div>
                  ))}
                </div>

                <div className="space-y-3">
                  {enrollments.map((e) => {
                    const moduleId = typeof e.module === 'object' ? (e.module?.id) : e.module;
                    const moduleName = e.module_name || e.module?.name || e.module?.title || `Module ${moduleId}`;
                    const isHighlighted = highlightId === e.id;
                    const isRejected = String(e.status).toLowerCase() === 'rejected';

                    return (
                      <Card
                        key={e.id}
                        id={`enrollment-row-${e.id}`}
                        padded={false}
                        className={`p-4 sm:p-5 transition ${isHighlighted ? 'ring-2 ring-accent-400' : ''}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Link to={`/modules/${moduleId}`} className="font-semibold text-gray-900 hover:text-primary-600 transition truncate">
                                {moduleName}
                              </Link>
                              <Badge status={e.status} />
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              {[e.grade_name, e.subscription_name].filter(Boolean).join(' · ')}
                            </p>
                          </div>

                          <div className="flex sm:flex-col sm:items-end gap-x-4 gap-y-1 text-xs text-gray-500 shrink-0">
                            <span>{t('Requested')}: {e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}</span>
                            <span>{t('Updated')}: {e.updated_at ? new Date(e.updated_at).toLocaleDateString() : '—'}</span>
                          </div>
                        </div>

                        {isRejected && e.rejection_reason && (
                          <div className="mt-3 bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-3 py-2">
                            {e.rejection_reason}
                          </div>
                        )}

                        {e.completion_percentage != null && (
                          <div className="mt-3 flex items-center gap-3">
                            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary-500 rounded-full transition-all"
                                style={{ width: `${e.completion_percentage}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-400 shrink-0">{e.completion_percentage}%</span>
                          </div>
                        )}

                        {e.completion_percentage >= 100 && (
                          <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-green-100 text-green-700 flex items-center justify-center shrink-0">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-green-900">{t('Congratulations!')}</p>
                              <p className="text-sm text-green-700">{t('You completed this course.')}</p>
                              {certificatesEnabled && e.certificate_status === 'pending' && (
                                <p className="text-xs text-green-700 mt-1">{t('Your certificate request is awaiting teacher approval.')}</p>
                              )}
                            </div>
                            {certificatesEnabled && (e.has_certificate ? (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => handleDownloadCertificate(e.id)}
                                loading={downloadingId === e.id}
                              >
                                {t('Download Certificate')}
                              </Button>
                            ) : e.certificate_status === 'pending' ? (
                              <span className="text-sm font-semibold text-amber-700 bg-amber-100 rounded-lg px-3 py-2 shrink-0">
                                {t('Approval Pending')}
                              </span>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleRequestCertificate(e.id)}
                                loading={requestingId === e.id}
                              >
                                {t('Request Certificate')}
                              </Button>
                            ))}
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default MyEnrollments;
